import { createRouter, createWebHistory } from 'vue-router'
import { fetchConfig } from '@/api/config'
import { getAuth0Client, waitForAuthReady } from '@/auth0-client'
import { fetchSSMConfig, isLoginLockdownEnabled, getEmergencyRedirectUrl } from '@/api/ssm'
import type { AppConfig } from '@/types/config'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login-lockdown',
      name: 'login-lockdown',
      component: () => import('@/views/LoginLockdownView.vue'),
      meta: { public: true },
    },
    /* Root: top-level domain (e.g. https://stillup.dev). Logged out → landing; logged in → dashboard. */
    {
      path: '/',
      name: 'landing',
      component: () => import('@/views/RootView.vue'),
      meta: { public: true },
    },
    {
      path: '/callback',
      name: 'callback',
      component: () => import('@/views/CallbackView.vue'),
      meta: { public: true },
    },
    {
      path: '/downloads',
      name: 'downloads',
      component: () => import('@/views/DownloadsView.vue'),
      meta: { public: true },
    },
    {
      path: '/downloads/',
      redirect: { name: 'downloads' },
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('@/views/PrivacyView.vue'),
      meta: { public: true },
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/views/TermsView.vue'),
      meta: { public: true },
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('@/views/AboutView.vue'),
      meta: { public: true },
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('@/views/ContactView.vue'),
      meta: { public: true },
    },
    /* Dashboard routes at top level: /profile, /devices, etc. */
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [{ path: '', component: () => import('@/views/ProfileView.vue') }],
    },
    {
      path: '/subscriptions',
      name: 'subscriptions',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [{ path: '', component: () => import('@/views/SubscriptionsView.vue') }],
    },
    {
      path: '/devices',
      name: 'devices',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [{ path: '', component: () => import('@/views/DevicesView.vue') }],
    },
    {
      path: '/devices/:deviceId',
      name: 'device-detail',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [{ path: '', component: () => import('@/views/DeviceDetailView.vue') }],
    },
    {
      path: '/devices/:deviceId/diagnostics',
      name: 'device-diagnostics',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [{ path: '', component: () => import('@/views/DeviceDiagnosticsView.vue') }],
    },
    {
      path: '/dashboards',
      name: 'dashboards',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [{ path: '', component: () => import('@/views/DashboardsView.vue') }],
    },
    {
      path: '/organizations',
      name: 'organizations',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [{ path: '', component: () => import('@/views/OrganizationsView.vue') }],
    },
  ],
})

export interface ConfigState {
  config: AppConfig | null
  ready: boolean
}

let configPromise: Promise<AppConfig> | null = null

export async function ensureConfig(): Promise<AppConfig> {
  if (!configPromise) configPromise = fetchConfig()
  return configPromise
}

/** Fetch SSM config (banner, emergency redirect, login lockdown). Called on each navigation; API caches for 1 min. */
export async function ensureSSMConfig(): Promise<void> {
  await fetchSSMConfig().catch(() => {})
}

const LAST_PATH_KEY = 'stillup_last_path'

router.beforeEach(async (to) => {
  await ensureConfig()
  await ensureSSMConfig() /* refetch on each nav; ssm API caches 1 min */
  await waitForAuthReady()
  
  const client = getAuth0Client()
  const isAuthenticated = client?.isAuthenticated.value ?? false
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)
  
  // Check for emergency redirect first
  const emergencyUrl = getEmergencyRedirectUrl()
  if (emergencyUrl && to.path !== emergencyUrl) {
    return { path: emergencyUrl, replace: true }
  }
  
  // Check for login lockdown
  if (isLoginLockdownEnabled() && requiresAuth && isAuthenticated) {
    return { name: 'login-lockdown' }
  }
  
  if (requiresAuth && !isAuthenticated) {
    return { name: 'landing', query: { redirect: to.fullPath } }
  }
  if (to.name === 'landing' && isAuthenticated && !to.query.redirect) {
    try {
      const last = localStorage.getItem(LAST_PATH_KEY)
      if (last && last !== '/') return { path: last }
    } catch {
      // ignore
    }
    /* Stay on / so RootView shows dashboard */
  }
  return true
})

router.afterEach((to) => {
  const isDashboardPath = to.path === '/' || to.matched.some((r) => r.meta.requiresAuth)
  if (isDashboardPath && to.path !== '/callback') {
    try {
      localStorage.setItem(LAST_PATH_KEY, to.fullPath)
    } catch {
      // ignore
    }
  }
})

export default router
